/**
 * Custom Content Form Component
 * Form for entering custom product content including ingredients, instructions, and messaging
 */

import { Input } from './ui';
import { CustomProductContent, ENHANCED_PRODUCT_INFO_CONSTANTS } from '../../../backend/src/types/enhanced-product-info.types';

interface CustomContentFormProps {
  customContent: Partial<CustomProductContent>;
  onChange: (field: keyof CustomProductContent, value: any) => void;
  disabled?: boolean;
  errors: { [key: string]: string };
}

export default function CustomContentForm({
  customContent,
  onChange,
  disabled = false,
  errors
}: CustomContentFormProps) {

  const handleAllergenChange = (allergen: string, checked: boolean) => {
    const currentAllergens = customContent.allergens || [];
    if (checked) {
      onChange('allergens', [...currentAllergens, allergen]);
    } else {
      onChange('allergens', currentAllergens.filter(a => a !== allergen));
    }
  };

  const commonAllergens = [
    'Milk', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 'Peanuts', 'Wheat', 'Soybeans', 'Sesame'
  ];

  return (
    <div>
      <h4 className="text-md font-medium text-gray-900 mb-4">Custom Product Content</h4>
      <div className="space-y-4">
        {/* Ingredients */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ingredients List
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical text-gray-900 placeholder-gray-500 bg-white"
            rows={3}
            value={customContent.ingredients || ''}
            onChange={(e) => onChange('ingredients', e.target.value)}
            placeholder="List ingredients in order of quantity (e.g., Organic milk, salt, bacterial cultures...)"
            disabled={disabled}
            maxLength={ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INGREDIENTS_LENGTH}
            style={{ color: '#111827' }}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.ingredients && (
              <p className="text-sm text-red-600">{errors.ingredients}</p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {(customContent.ingredients || '').length}/{ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INGREDIENTS_LENGTH}
            </p>
          </div>
        </div>

        {/* Allergens */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allergen Information
          </label>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {commonAllergens.map((allergen) => (
              <label key={allergen} className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={(customContent.allergens || []).includes(allergen)}
                  onChange={(e) => handleAllergenChange(allergen, e.target.checked)}
                  disabled={disabled}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{allergen}</span>
              </label>
            ))}
          </div>
          {(customContent.allergens || []).length > 0 && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
              <p className="text-xs text-amber-800">
                <strong>Contains:</strong> {(customContent.allergens || []).join(', ')}
              </p>
            </div>
          )}
        </div>

        {/* Usage Instructions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Usage Instructions
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical text-gray-900 placeholder-gray-500 bg-white"
            rows={3}
            value={customContent.usageInstructions || ''}
            onChange={(e) => onChange('usageInstructions', e.target.value)}
            placeholder="How to use this product (e.g., Shake well before use. Refrigerate after opening...)"
            disabled={disabled}
            maxLength={ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INSTRUCTIONS_LENGTH}
            style={{ color: '#111827' }}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.usageInstructions && (
              <p className="text-sm text-red-600">{errors.usageInstructions}</p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {(customContent.usageInstructions || '').length}/{ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INSTRUCTIONS_LENGTH}
            </p>
          </div>
        </div>

        {/* Storage Instructions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Storage Instructions
          </label>
          <Input
            value={customContent.storageInstructions || ''}
            onChange={(e) => onChange('storageInstructions', e.target.value)}
            placeholder="e.g., Store in a cool, dry place. Refrigerate after opening."
            disabled={disabled}
          />
        </div>

        {/* Custom Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Message to Consumers
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical text-gray-900 placeholder-gray-500 bg-white"
            rows={4}
            value={customContent.customMessage || ''}
            onChange={(e) => onChange('customMessage', e.target.value)}
            placeholder="Share a message with your consumers (e.g., Thank you for choosing our organic products...)"
            disabled={disabled}
            maxLength={ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CUSTOM_MESSAGE_LENGTH}
            style={{ color: '#111827' }}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.customMessage && (
              <p className="text-sm text-red-600">{errors.customMessage}</p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {(customContent.customMessage || '').length}/{ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CUSTOM_MESSAGE_LENGTH}
            </p>
          </div>
        </div>

        {/* Brand Story */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brand Story
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical text-gray-900 placeholder-gray-500 bg-white"
            rows={4}
            value={customContent.brandStory || ''}
            onChange={(e) => onChange('brandStory', e.target.value)}
            placeholder="Tell your brand's story (e.g., Family-owned since 1950, committed to sustainable farming...)"
            disabled={disabled}
            maxLength={ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_BRAND_STORY_LENGTH}
            style={{ color: '#111827' }}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.brandStory && (
              <p className="text-sm text-red-600">{errors.brandStory}</p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {(customContent.brandStory || '').length}/{ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_BRAND_STORY_LENGTH}
            </p>
          </div>
        </div>

        {/* Helper Text */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-xs text-green-800">
            <strong>Tip:</strong> This information will be displayed to consumers when they scan your product's QR code. 
            Use clear, helpful language that builds trust and provides value.
          </p>
        </div>
      </div>
    </div>
  );
}